import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import Cookies from 'universal-cookie';
import { toast } from 'react-toastify';
import { AUTH_EXPIRED_EVENT, notifyAuthExpired, resetAuthExpiry, suppressAuthExpiry } from "api/authEvents";
const cookie = new Cookies();
const AuthContext = createContext();

// The access token lives 10 days server-side (SIMPLE_JWT.ACCESS_TOKEN_LIFETIME).
// The cookies used to be session cookies with no `expires`, so they vanished on
// browser close while the JWT was still valid — the user looked signed out, or
// worse, state and cookie disagreed. Computed from now; we deliberately do not
// decode the JWT for this.
const TOKEN_LIFETIME_DAYS = 10;

/**
 * Attributes shared by every auth cookie, for both writing and removal, so the
 * flags can never drift per cookie.
 *
 * `secure` is applied only over https, which keeps local http:// development
 * working while every deployed environment gets the flag — a `Secure` cookie is
 * simply ignored by the browser on a plain-http origin, which would silently
 * break dev logins.
 *
 * `sameSite: 'lax'` rather than 'strict' is deliberate, and the reason is this
 * file's own bug. Under 'strict' the browser withholds cookies on ANY cross-site
 * top-level navigation, so an external inbound link to a protected route — a
 * mailed link to a purchase order, a Slack link to an issuance — arrives
 * cookie-less. `token` state then initialises to "" from an absent cookie while
 * the cookie itself is still very much alive and reappears on the next
 * same-site navigation: precisely the state/cookie desync this branch exists to
 * eliminate, reintroduced by a cookie attribute. 'lax' sends cookies on
 * top-level GETs and still blocks cross-site POSTs, which is the CSRF shape that
 * actually matters here.
 *
 * (Note the public email-link routes — /reset-password, /invitation,
 * /forgot-password, /service/leave-request — are NOT the reason: they call
 * login=false APIs authenticated by a query-param token and never read an auth
 * cookie at all.)
 *
 * There is no `domain` either, and that is deliberate. Setting
 * `domain: window.location.hostname` makes a domain cookie, which is a DIFFERENT
 * cookie from the host-only one an earlier build wrote under the same name. A
 * browser will hold both at once, `document.cookie` returns both, and a parser
 * has to pick one — so a login could write a fresh token into one while
 * every request read the stale one out of the other, and logout (which passed
 * the domain) removed only its own. That is not theoretical: on 2026-09-10 a
 * dev tab sent a two-day-old token on every request while its login kept
 * succeeding, because api-dev had been redeployed with a new SECRET_KEY and the
 * good token never got read back.
 *
 * Host-only is also what this app actually wants: nothing here is shared with a
 * sibling subdomain. `removeAuthCookie` below still deletes the legacy domain
 * shape, so an existing duplicate dies on the next logout or expiry rather than
 * outliving it.
 */
const baseCookieOptions = () => {
  const isHttps = window.location.protocol === 'https:';
  return {
    path: '/',
    ...(isHttps ? { secure: true, sameSite: 'lax' } : {}),
  };
};

/**
 * Delete a cookie in BOTH shapes: host-only (what we write now) and the legacy
 * domain cookie earlier builds wrote. A remove only matches a cookie whose
 * path and domain agree, so clearing one shape leaves the other in place.
 */
const removeAuthCookie = (name) => {
  const options = baseCookieOptions();
  cookie.remove(name, options);
  cookie.remove(name, { ...options, domain: window.location.hostname });
};

const cookieOptions = () => ({
  ...baseCookieOptions(),
  expires: new Date(Date.now() + TOKEN_LIFETIME_DAYS * 24 * 60 * 60 * 1000),
});

// path/domain must match what login() set, or the browser silently keeps the
// cookie. Kept as one list so logout and session-expiry can never drift apart.
//
// `store` was NOT in the old logout list — a pre-existing leak that this change
// would otherwise make worse: now that cookies persist 10 days instead of dying
// with the browser session, a shared machine would hand the next user the
// previous user's default store whenever their own login carries no default.
const AUTH_COOKIE_NAMES = ["token", "refresh", "is_crew", "is_user", "is_customer", "user", "store"];

const clearAuthCookies = () => {
  AUTH_COOKIE_NAMES.forEach(removeAuthCookie);
};

const SESSION_ENDED_MESSAGE = "Your session has ended — please sign in again";

const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(cookie.get('token')||"");

    // Read by the visibility/focus listeners, which are registered once. Using a
    // ref keeps them off the token dependency so we are not tearing down and
    // re-adding window listeners on every auth state change.
    const tokenRef = useRef(token);
    useEffect(() => { tokenRef.current = token; }, [token]);

    /**
     * One-time scrub of the legacy PII payload.
     *
     * Without this, only users who sign in afresh get the slimmed cookie —
     * anyone already holding a valid session keeps email, phone and full name
     * on disk for up to the full 10 days, since they never call login() again.
     * Rewrites in place, keeping only the id, and leaves a well-formed cookie
     * alone so this costs one parse per load.
     */
    useEffect(() => {
      const raw = cookie.get('user');
      if (!raw) return;
      try {
        // universal-cookie auto-parses JSON-looking values, so this may already
        // be an object rather than a string.
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (parsed && typeof parsed === "object" && Object.keys(parsed).length > 1) {
          // baseCookieOptions(), not cookieOptions(): no `expires`, so a legacy
          // session cookie stays a session cookie. The scrub must never extend a
          // lifetime it did not create.
          cookie.set('user', JSON.stringify({ id: parsed.id }), baseCookieOptions());
        }
      } catch (e) {
        // Unparseable legacy value: drop it rather than leave unknown content.
        removeAuthCookie('user');
      }
    }, []);

    /**
     * The one path out of an expired session, shared by the 401 interceptor and
     * the cookie/state sync below.
     *
     * There is no navigate() call here on purpose: AuthProvider sits outside
     * <BrowserRouter> (see src/index.jsx), so no router hook is available. It
     * does not need one — ProtectedRoute renders <Navigate to="/login" /> the
     * moment token is falsy, so clearing state *is* the redirect. That is also
     * strictly better than window.location: a hard navigation would unmount
     * ToastContainer and the user would never see the toast explaining why they
     * were kicked out.
     *
     * toastId de-dupes at the react-toastify level as a second line of defence
     * behind the latch in authEvents.
     */
    const handleSessionExpired = useCallback(() => {
      clearAuthCookies();
      setToken("");
      toast.error(SESSION_ENDED_MESSAGE, { toastId: "auth-session-expired" });
    }, []);

    useEffect(() => {
      const onAuthExpired = () => handleSessionExpired();
      window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
      return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
    }, [handleSessionExpired]);

    /**
     * Cookie/state reconciliation.
     *
     * If another tab logs out, or the websocket logout handler clears the cookie,
     * this tab's React state still says "signed in" and the guard happily keeps
     * the user on the page until some request fails. Re-reading the cookie when
     * the tab becomes visible or regains focus closes that gap to about a second.
     *
     * Note there is no `storage` listener: auth here is entirely cookie-based.
     * The only localStorage writes are UI preferences (colorschema, layout*) plus
     * `company`; every localStorage auth line (token/accessToken/credencial) in
     * this codebase is commented out and dead. `storage` does not fire for cookie
     * changes, so a listener would be pure noise.
     */
    useEffect(() => {
      const syncWithCookie = () => {
        if (tokenRef.current && !cookie.get('token')) {
          // Route through the latch so this and an in-flight 401 cannot both
          // report the same expiry. dispatchEvent is synchronous, so the
          // listener above has already run handleSessionExpired by the time
          // this returns — calling it here as well would just double-invoke.
          notifyAuthExpired("cookie-missing");
        }
      };
      const onVisibilityChange = () => {
        if (document.visibilityState === "visible") syncWithCookie();
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
      window.addEventListener("focus", syncWithCookie);
      return () => {
        document.removeEventListener("visibilitychange", onVisibilityChange);
        window.removeEventListener("focus", syncWithCookie);
      };
      // No deps: reads state through tokenRef and only calls module-level
      // functions, so these listeners are registered exactly once.
    }, []);

    const logout = () => {
      // Suppress FIRST, before the cookies go. Between clearing cookies and
      // react-router committing <Navigate to="/login"/>, any request already in
      // flight lands 401 and is still tagged; without this it would toast
      // "your session has ended" over a logout the user deliberately performed.
      // Covers the websocket logout path too, which calls this same function.
      // The latch is re-armed on the next login(), not here.
      suppressAuthExpiry();
      clearAuthCookies();
      setToken("");
    }

    const login = async (data) => {
      // Clear first. A stale duplicate under a different domain/path shape
      // would otherwise survive the writes below and keep being the one read
      // back, so the app would send a dead token while login kept succeeding.
      clearAuthCookies();
      const options = cookieOptions();
      cookie.set('token', data.access, options);
      cookie.set('refresh', data.refresh, options);

      cookie.set('is_crew', data.is_crew_user, options);
      cookie.set('is_user', data.is_customer_user, options);

      cookie.set('is_customer', data.is_customer_user, options);
      // Identity only — no PII. This cookie used to carry email, phone number
      // and full name in clear JSON, readable by any script on the origin and
      // (since these cookies gained a 10-day expiry) sitting on disk rather than
      // dying with the browser session.
      //
      // Nothing in the app ever read those fields back: `token` is the only
      // cookie this codebase reads anywhere. The profile is already served from
      // memory by the `user` redux slice, which fetches GET api/ess/whoami/ via
      // fetchUser() in Routes/Appcontainer/index.jsx on every authenticated
      // load. Its consumers (Header, the asset/document/expense/resignation/
      // leave modals) already read through `user?.first_name || ""`, so they
      // degrade to blank before that request resolves rather than crashing.
      cookie.set('user', JSON.stringify({ id: data.id }), options);

      if(data.store){
        let default_store = data.store.find((item) => item.is_default)
        if(default_store){
          cookie.set('store', default_store.store.id.toString(), options);
        }
      }

      // A fresh session must be able to expire again later.
      resetAuthExpiry();
      setToken(data.access);

      localStorage.setItem("colorschema", "orange");
      localStorage.setItem("layout", "vertical");
      localStorage.setItem("layoutwidth", "fixed");
      localStorage.setItem("layoutpos", "fluid");
      localStorage.setItem("topbartheme", "light");
      localStorage.setItem("layoutSized", "lg");
      localStorage.setItem("layoutStyling", "default");
      localStorage.setItem("layoutSidebarStyle", "dark");
    };
  return (
    <AuthContext.Provider value={{ token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export { AuthContext, AuthProvider, useAuth };
